import { DateTime } from 'luxon';
import { useExternalParams } from 'shared/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { ProviderFactory } from 'shared/providers/provider-factory';
import { AvailabilityPageParamsContext } from './availability-page-params-provider.types';

const initialValues = {
  search: { initialValue: undefined, type: 'string' },
  date: { initialValue: undefined, type: 'date' },
} as const;

const [AvailabilityContextProviderComponent, useAvailabilityPageParamsProvider] =
  ProviderFactory<AvailabilityPageParamsContext>(
    'AvailabilityPageParamsContext can only be accessed within AvailabilityPageParamsProvider'
  );

type AvailabilityPageParamsProviderProps = {
  children: React.ReactNode;
};

export const AvailabilityPageParamsProvider = ({
  children,
}: AvailabilityPageParamsProviderProps) => {
  const [params, setParams] = useExternalParams(initialValues);
  const savedParamsRef = useRef(params);

  useEffect(() => {
    savedParamsRef.current = {
      ...savedParamsRef.current,
      search: params.search,
      date: params.date,
    };
  }, [params.search, params.date]);

  const filters = useMemo(
    () => ({
      search: params.search || undefined,
      date: params.date || undefined,
    }),
    [params]
  );

  const selectedFiltersCount = useMemo(
    () =>
      Object.values(filters).filter(
        (value) => value !== null && value !== undefined && value !== '' && !isDateTime(value)
      ).length,
    [filters]
  );

  return (
    <AvailabilityContextProviderComponent
      value={{
        filters,
        savedParamsRef,
        setFilters: setParams,
        selectedFiltersCount,
      }}
    >
      {children}
    </AvailabilityContextProviderComponent>
  );
};

export const useAvailabilityPageParamsContext = () => {
  return useAvailabilityPageParamsProvider();
};

const isDateTime = (filterValue: any): filterValue is DateTime => filterValue instanceof DateTime;
